<template>
  <v-autocomplete
    v-model="selectedSupplier"
    :items="availableSupplierList"
    :loading="isLoading"
    :disabled="disabled"
    :search-input.sync="supplierSearch"
    item-text="selectorText"
    return-object
    no-filter
    clearable
    outlined
    dense
    hide-details="auto"
    label="Proveedor o CIF"
    prepend-inner-icon="mdi-domain"
    no-data-text="No hay proveedores que coincidan"
    :hide-no-data="hideEmptySupplierList"
    :error-messages="searchErrorMessage"
    @focus="loadSupplierList('')"
    @change="selectSupplier"
  ></v-autocomplete>
</template>

<script>
import { ApiSupplierRepository, SUPPLIER_LOOKUP_ERROR_MESSAGE } from './ApiSupplierRepository';

export default {
  name: 'SupplierAutocomplete',
  props: {
    companyId: { type: Number, required: true },
    supplierTaxId: { type: String, default: '' },
    supplierName: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      supplierRepository: new ApiSupplierRepository(),
      supplierItemList: [],
      selectedSupplier: null,
      supplierSearch: '',
      supplierSearchTimer: null,
      activeSearchTerm: null,
      completedSearchTerm: null,
      searchRequestNumber: 0,
      isLoading: false,
      searchErrorMessage: '',
    };
  },
  computed: {
    // Vacia por un fallo, la lista no se abre: taparia el aviso del campo.
    hideEmptySupplierList() {
      return !!this.searchErrorMessage;
    },
    availableSupplierList() {
      if (!this.selectedSupplier) return this.supplierItemList;
      const isSelectedAvailable = this.supplierItemList.some(supplier => (
        supplier.id === this.selectedSupplier.id
        && supplier.cif === this.selectedSupplier.cif
      ));
      return isSelectedAvailable
        ? this.supplierItemList
        : [this.selectedSupplier].concat(this.supplierItemList);
    },
  },
  watch: {
    supplierTaxId: { immediate: true, handler() { this.syncSelectedSupplier(); } },
    supplierName: { immediate: true, handler() { this.syncSelectedSupplier(); } },
    supplierSearch(searchTerm) {
      if (
        this.selectedSupplier
        && searchTerm === this.selectedSupplier.selectorText
      ) return;
      this.scheduleSupplierSearch(searchTerm);
    },
  },
  beforeDestroy() {
    if (this.supplierSearchTimer) clearTimeout(this.supplierSearchTimer);
  },
  methods: {
    syncSelectedSupplier() {
      if (!this.supplierTaxId && !this.supplierName) {
        this.selectedSupplier = null;
        return;
      }
      if (
        this.selectedSupplier
        && this.selectedSupplier.cif === this.supplierTaxId
        && this.selectedSupplier.name === this.supplierName
      ) return;
      this.selectedSupplier = {
        id: null,
        cif: this.supplierTaxId,
        name: this.supplierName,
        selectorText: [this.supplierName, this.supplierTaxId].filter(Boolean).join(' — '),
      };
    },
    scheduleSupplierSearch(searchTerm) {
      if (this.supplierSearchTimer) clearTimeout(this.supplierSearchTimer);
      this.supplierSearchTimer = setTimeout(
        () => this.loadSupplierList(searchTerm),
        300,
      );
    },
    async loadSupplierList(searchTerm) {
      const normalizedSearchTerm = String(searchTerm || '').trim();
      if (
        normalizedSearchTerm === this.activeSearchTerm
        || normalizedSearchTerm === this.completedSearchTerm
      ) return false;

      const requestNumber = this.searchRequestNumber + 1;
      this.searchRequestNumber = requestNumber;
      this.activeSearchTerm = normalizedSearchTerm;
      this.isLoading = true;
      try {
        const supplierItemList = await this.supplierRepository.search(
          this.companyId,
          normalizedSearchTerm,
        );
        if (requestNumber !== this.searchRequestNumber) return false;
        this.supplierItemList = supplierItemList;
        this.completedSearchTerm = normalizedSearchTerm;
        this.searchErrorMessage = '';
        return true;
      } catch (error) {
        console.error('Supplier search failed:', error);
        // Un fallo no es «no hay proveedores»: se dice, para no crear uno repetido.
        if (requestNumber === this.searchRequestNumber) {
          this.supplierItemList = [];
          this.searchErrorMessage = `${SUPPLIER_LOOKUP_ERROR_MESSAGE} Vuelva a escribir para intentarlo de nuevo.`;
          // Tras un fallo, la lista ya no es la de la ultima busqueda buena: repetirla vuelve a preguntar.
          this.completedSearchTerm = null;
        }
        return false;
      } finally {
        if (requestNumber === this.searchRequestNumber) {
          this.activeSearchTerm = null;
          this.isLoading = false;
        }
      }
    },
    selectSupplier(supplier) {
      this.$emit('select', supplier || null);
    },
  },
};
</script>
